import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { LANGUAGES } from '@edtech-ui/core/constants/language.constants';
import { ResumeStepValidatable } from '@edtech-ui/core/models/resume-builder/step-validators';
import { ResumeService } from '@edtech-ui/core/services/resume-service/resume.service';

@Component({
	selector: 'app-resumer-builder-extras',
	standalone: true,
	imports: [ReactiveFormsModule, CommonModule, FormsModule],
	templateUrl: './resumer-builder-extras.component.html',
	styleUrl: './resumer-builder-extras.component.scss'
})
export class ResumerBuilderExtrasComponent implements OnInit, ResumeStepValidatable {

	languageOptions = LANGUAGES;
	currentYear = new Date().getFullYear();

	constructor(
		private resumeService: ResumeService,
		private route: ActivatedRoute
	) { }

	ngOnInit(): void {
		const resumeId = this.route.snapshot.paramMap.get('resumeId');
		if (resumeId) {
			this.getExtrasFromAPi();
		} else {
			this.getExtrasFromService();
		}
	}

	getExtrasFromAPi(): void {
		this.resumeService.getFullResumeData().subscribe(data => {
			if (data?.extras) {
				this.editExtrasData(data.extras);
			}
		});
	}

	getExtrasFromService(): void {

		const data = this.resumeService.getSectionData('extras');
		if (data) {
			this.editExtrasData(data);
		}
	}

	userForm = new FormGroup({
		certificates: new FormArray([]),
		awards: new FormArray([]),
		hackathons: new FormArray([]),
		languages: new FormArray([])
	});


	get certificates() {
		return this.userForm.get('certificates') as FormArray;
	}


	get awards() {
		return this.userForm.get('awards') as FormArray;
	}


	get hackathons() {
		return this.userForm.get('hackathons') as FormArray;
	}


	get languages() {
		return this.userForm.get('languages') as FormArray;
	}


	createCertificate() {
		return new FormGroup({
			name: new FormControl('', Validators.required),
			platform: new FormControl(''),
			year: new FormControl('',[
				Validators.pattern(/^\d{4}$/),  Validators.max(this.currentYear)
			])
		});
	}


	createAward() {
		return new FormGroup({
			name: new FormControl('', Validators.required),
			org: new FormControl('')
		});
	}


	createHackathon() {
		return new FormGroup({
			name: new FormControl('', Validators.required),
			rank: new FormControl('')
		});
	}


	createLanguage() {
		return new FormGroup({
			name: new FormControl('', Validators.required),
			level: new FormControl('')
		});
	}


	addCertificate() {
		this.certificates.push(this.createCertificate());
	}


	removeCertificate(i: number) {
		this.certificates.removeAt(i);
	}


	addAward() {
		this.awards.push(this.createAward());
	}


	removeAward(i: number) {
		this.awards.removeAt(i);
	}


	addHackathon() {
		this.hackathons.push(this.createHackathon());
	}


	removeHackathon(i: number) {
		this.hackathons.removeAt(i);
	}


	addLanguage() {
		this.languages.push(this.createLanguage());
	}


	removeLanguage(i: number) {
		this.languages.removeAt(i);
	}


	saveData() {
		if (this.userForm.invalid) {
			this.userForm.markAllAsTouched();
			return;
		}
		const formValue = this.userForm.value;

		const payload = {
			certifications: (formValue.certificates || []).map((c: any) => ({
				name: c.name,
				platform: c.platform,
				year: c.year?.toString() || ''
			})),
			awards: (formValue.awards || []).map((a: any) => ({
				name: a.name,
				organization: a.org
			})),
			hackathons: formValue.hackathons || [],
			languages: (formValue.languages || []).map((lang: any) => ({
				name: lang.name,
				...(lang.level ? { proficiency: lang.level } : {})
			}))
		};
		this.resumeService.setSectionData('extras', payload);
	}


	editExtrasData(data: any) {
		if (!data) return;
		this.certificates.clear();
		this.awards.clear();
		this.hackathons.clear();
		this.languages.clear();

		if (data.certifications?.length) {
			data.certifications.forEach((c: any) => {
				this.certificates.push(
					new FormGroup({
						name: new FormControl(c.name || '', Validators.required),
						platform: new FormControl(c.platform || ''),
						year: new FormControl(c.year || '')
					})
				);
			});
		}

		if (data.awards?.length) {
			data.awards.forEach((a: any) => {
				this.awards.push(
					new FormGroup({
						name: new FormControl(a.name || '', Validators.required),
						org: new FormControl(a.organization || '')
					})
				);
			});
		}

		if (data.hackathons?.length) {
			data.hackathons.forEach((h: any) => {
				this.hackathons.push(
					new FormGroup({
						name: new FormControl(h.name || '', Validators.required),
						rank: new FormControl(h.rank || '')
					})
				);
			});
		}

		if (data.languages?.length) {
			data.languages.forEach((lang: any) => {
				this.languages.push(
					new FormGroup({
						name: new FormControl(lang.name || '', Validators.required),
						level: new FormControl(lang.proficiency || '')
					})
				);
			});
		}

		if (!this.certificates.length) this.addCertificate();
		if (!this.awards.length) this.addAward();
		if (!this.hackathons.length) this.addHackathon();
		if (!this.languages.length) this.addLanguage();
	}

	getControl(array: FormArray, index: number, field: string) {
		return array.at(index).get(field);
	}


	isFormValid(): boolean {
		return this.userForm.valid;
	}

	onYearInput(event: any) {
		let value = event.target.value.replace(/[^0-9]/g, '');
		if (value.length > 4) {
			value = value.slice(0, 4);
		}
		event.target.value = value;
	}


}